/**
 * ToolErrorMessage - Phase 8: UX Review (Story 8.1)
 * BMad Agent: Sally (UX), UI Designer
 * 
 * User-friendly error messages when tools fail
 * NO technical jargon - clear actions users can take
 */

import React from 'react';
import { AlertCircle, RefreshCw, MessageCircle, HelpCircle, XCircle, Clock, WifiOff } from 'lucide-react';

export type ToolErrorType = 
  | 'web_search_failed'
  | 'verification_timeout'
  | 'knowledge_unavailable'
  | 'translation_failed'
  | 'document_generation_failed'
  | 'connection_lost'
  | 'rate_limited'
  | 'generic';

export interface ToolError {
  type: ToolErrorType;
  toolName?: string;
  technicalDetails?: string;
  timestamp: Date;
}

interface ToolErrorMessageProps {
  error: ToolError;
  onRetry?: () => void;
  onAskDifferently?: () => void;
  onDismiss?: () => void;
  className?: string;
}

// Sally's UX-approved error messages (friendly, actionable, no jargon)
const ERROR_MESSAGES: Record<ToolErrorType, {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  primaryAction?: string;
  secondaryAction?: string;
}> = {
  web_search_failed: {
    title: "Couldn't find web information",
    description: "I wasn't able to search the web right now. I'll use my existing knowledge to help you instead.",
    icon: WifiOff,
    primaryAction: "Try again",
    secondaryAction: "Continue without web search"
  },
  verification_timeout: {
    title: "Verification taking too long",
    description: "Fact-checking is taking longer than expected. I can provide an answer based on what I know, but it may not be fully verified.",
    icon: Clock,
    primaryAction: "Keep waiting",
    secondaryAction: "Continue without verification"
  },
  knowledge_unavailable: {
    title: "Knowledge base temporarily unavailable",
    description: "I can't access the company knowledge base right now. I'll help you with my general knowledge instead.",
    icon: AlertCircle,
    primaryAction: "Try again",
    secondaryAction: "Ask differently"
  },
  translation_failed: {
    title: "Translation didn't work",
    description: "I couldn't complete the translation. You can try again or ask me to translate a shorter section.",
    icon: XCircle,
    primaryAction: "Try again",
    secondaryAction: "Try shorter text"
  },
  document_generation_failed: {
    title: "Document creation failed",
    description: "I couldn't create the document. You can try again or I can provide the content as text instead.",
    icon: XCircle,
    primaryAction: "Try again",
    secondaryAction: "Get as text"
  },
  connection_lost: {
    title: "Connection interrupted",
    description: "We lost connection briefly. Your conversation is saved and I'm reconnecting now.",
    icon: WifiOff,
    primaryAction: "Reconnect",
    secondaryAction: "Dismiss"
  },
  rate_limited: {
    title: "Taking a short break",
    description: "I'm processing a lot of requests. Please wait a moment and I'll be ready to help again.",
    icon: Clock,
    primaryAction: "Wait",
    secondaryAction: "Dismiss"
  },
  generic: {
    title: "Something went wrong",
    description: "I encountered an issue but I'm still here to help. You can try your request again or ask in a different way.",
    icon: AlertCircle,
    primaryAction: "Try again",
    secondaryAction: "Ask differently"
  }
};

/**
 * ToolErrorMessage component
 * Shows user-friendly error messages with clear actions
 */
export function ToolErrorMessage({
  error,
  onRetry,
  onAskDifferently,
  onDismiss,
  className = ''
}: ToolErrorMessageProps) {
  const config = ERROR_MESSAGES[error.type] || ERROR_MESSAGES.generic;
  const Icon = config.icon;
  
  return (
    <div className={`p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg ${className}`}>
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-full">
          <Icon className="w-5 h-5 text-amber-600 dark:text-amber-300" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-amber-900 dark:text-amber-100">
            {config.title}
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
            {config.description}
          </p>
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 mt-3">
            {config.primaryAction && onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                {config.primaryAction}
              </button>
            )}
            
            {config.secondaryAction && onAskDifferently && (
              <button
                onClick={onAskDifferently}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-200 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-md transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                {config.secondaryAction}
              </button>
            )}
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="ml-auto text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200"
                aria-label="Dismiss"
              >
                <XCircle className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Help link */}
          <button className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 hover:underline mt-3">
            <HelpCircle className="w-3 h-3" />
            Why did this happen?
          </button>
        </div>
      </div>
    </div>
  );
}

export default ToolErrorMessage;
